import { of, interval, from } from 'rxjs'
import {
  switchMap,
  map,
  catchError,
  tap,
  mapTo,
  mergeMap,
  concatMap,
  filter,
  withLatestFrom,
  throttleTime
} from 'rxjs/operators'
import { ofType } from 'redux-observable'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import {
  getJSON,
  postJSON,
  setAuthToken,
  hasAuthToken,
  removeAuthToken,
  setUserOnBoarded,
  hasOnBoarded,
  patchJSON,
  putJSON
} from '../utils/fetchUtils'
import { routes, signUpFlowTypes, signUpStatus } from '../consts'
import { CUSTOME_ERRORS, translateErrorMsg } from '../utils/customErrorHandler'
import { event, eventTypes } from '../consts/eventConsts'
import { uriEncodeSearchParams } from '../utils/encodingUtils'

export const checkSignupAuthentication = action$ => {
  return action$.pipe(
    getPayload(Actions.SIGNUP_RECEIVED),
    switchMap(() =>
      hasAuthToken().pipe(
        map(isSignedIn =>
          Actions.createAction(Actions.IS_SIGNED_IN_RECEIVED, {
            isSignedIn
          })
        ),
        catchError(() =>
          of(
            Actions.createAction(Actions.IS_SIGNED_IN_RECEIVED, {
              isSignedIn: false
            })
          )
        )
      )
    )
  )
}

export const requestSignUpPasswordStepEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SIGNUP_PASSWORD_REQUESTED),
    withLatestFrom(state$),
    filter(([_, state]) => state.auth.signupFlowType !== signUpFlowTypes.HMO),
    switchMap(([{ password, userSignup, pendingPatient }, state]) => {
      const { firstname, lastname, dob, phoneNumber, email } = userSignup
      const isPendingPatient =
        pendingPatient !== undefined && pendingPatient !== null
      const data = {
        username: email,
        password,
        date_of_birth: dob,
        profile: {
          phone_number: phoneNumber,
          first_name: firstname,
          last_name: lastname,
          hashed_user_profile: state.signup?.hmoFirstLogin?.userId
        },
        is_pending_patient: isPendingPatient,
        user_type: 1
      }
      return postJSON('/core/signup/', data).pipe(
        // Ensure token is persisted before dispatching authenticated actions
        concatMap(tokenData =>
          from(setAuthToken(tokenData)).pipe(
            tap(() => setUserOnBoarded()),
            mergeMap(() => {
              // if (isMobileWeb()) {
              //   return of(Actions.createAction(Actions.IS_MOBILE_WEB_USER))
              // } else {
              delete data['password']
              return of(
                Actions.createAction(Actions.CHECK_FOR_VALID_IP, {
                  isSignup: true
                }),
                Actions.createAction(Actions.SIGNUP_RECEIVED),
                Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                  type: eventTypes.formEvent,
                  eventName: event.formEvent.submit,
                  data: data,
                  element: 'Choose Password'
                })
                // Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                //   eventName: Actions.SIGNUP_RECEIVED + '_WEB',
                // })
              )
              // }
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.SIGNUP_RECEIVED_FAILED,
              translateErrorMsg(e)
            )
          )
        }),
        catchAndDispatchCustomError(data, 'Choose Password')
      )
    })
  )

export const isIpChangedOnReload = action$ =>
  action$.pipe(
    getPayload(Actions.IS_IP_CHANGED_ON_RELOAD),
    mapTo(Actions.createAction(Actions.VALIDATE_COUNTRY_REQUESTED))
  )

export const ipValidationEpic = action$ =>
  action$.pipe(
    getPayload(Actions.CHECK_FOR_VALID_IP),
    switchMap(({ isSignup }) => {
      if (isSignup) {
        return of(Actions.createAction(Actions.VALIDATE_COUNTRY_REQUESTED))
      } else {
        return of(
          Actions.createAction(Actions.VALIDATE_COUNTRY_REQUESTED),
          Actions.createAction(Actions.IS_SIGNED_IN_RECEIVED, {
            isSignedIn: true
          })
        )
      }
    })
  )

export const ipValidationCheckEpic = action$ =>
  action$.pipe(
    getPayload(Actions.VALIDATE_COUNTRY_REQUESTED),
    switchMap(() => {
      return getJSON('/is-valid-ip/').pipe(
        mergeMap(result => {
          if (result?.is_valid_ip) {
            return of(
              Actions.createAction(Actions.VALIDATE_COUNTRY_RECEIVED, result)
            )
          } else {
            return of(Actions.createAction(Actions.INVALID_IP_RECIEVED))
          }
        }),
        catchError(e => console.error(e))
      )
    })
  )

export const requestSignUpEmailStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SIGNUP_EMAIL_REQUESTED),
    switchMap(
      ({ email, urlParams, phoneNumber, firstName, lastName, dateOfBirth }) => {
        let lowerEmail = email.toLowerCase()
        return postJSON(`/core/check_username/${lowerEmail}/`, {
          user_type: 1,
          url_params: urlParams,
          phoneNumber: phoneNumber,
          firstName: firstName,
          lastName: lastName
        }).pipe(
          mergeMap(({ lastDigits }) => {
            if (lastDigits) {
              return of(
                Actions.createAction(Actions.SIGNUP_EMAIL_RECEIVED, { email }),
                Actions.createAction(Actions.PENDING_PATIENT_LASTDIGITS, {
                  lastDigits
                })
              )
            }
            return of(
              Actions.createAction(Actions.SIGNUP_PHONENUMBER_REQUESTED, {
                phoneNumber: phoneNumber,
                email: email,
                firstName,
                lastName,
                dateOfBirth
              }),
              Actions.createAction(Actions.SIGNUP_EMAIL_RECEIVED, { email }),
              Actions.createAction(Actions.SIGNUP_EMAIL_SUCCESS)
            )
          }),
          catchAndDispatchCustomError({
            firstName,
            lastName,
            email,
            dateOfBirth,
            phoneNumber
          })
        )
      }
    )
  )

export const requestOEMemberPrefilledDataEpic = action$ =>
  action$.pipe(
    getPayload(Actions.GET_PREFILL_FIELDS_FOR_SIGNUP_REQUESTED),
    switchMap(() => {
      return getJSON('/zuora/get-member-data/').pipe(
        switchMap(result =>
          of(
            Actions.createAction(
              Actions.GET_PREFILL_FIELDS_FOR_SIGNUP_RECEIVED,
              {
                prefillFields: result
              }
            )
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(Actions.GET_PREFILL_FIELDS_FOR_SIGNUP_FAILED)
          )
        })
      )
    })
  )

export const requestClassifyUserSignUp = action$ =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_REQUESTED),
    switchMap(
      ({
        email,
        firstName,
        middleName,
        lastName,
        phoneNumber,
        zipCode,
        dob,
        transactionId
      }) => {
        return postJSON('/signup/classify-user/', {
          email,
          firstName,
          middleName,
          lastName,
          phoneNumber,
          zipCode,
          dob,
          transaction_id: transactionId
        }).pipe(
          map(({ classify_status, user }) =>
            Actions.createAction(Actions.CLASSIFY_USER_RECEIVED, {
              classify_status,
              user
            })
          ),
          catchAndDispatchCustomError(
            {
              email,
              firstName,
              middleName,
              lastName,
              phoneNumber,
              zipCode,
              dob
            },
            'next information account setup'
          )
        )
      }
    )
  )

export const requestSignUpEmailVerifyEpic = action$ =>
  action$.pipe(
    getPayload(Actions.EMAIL_VERIFY_REQUESTED),
    throttleTime(5000),
    filter(({ email }) => email),
    switchMap(({ email }) => {
      let lowerEmail = email.toLowerCase()
      return getJSON(`/core/check_email_verified/${lowerEmail}/`).pipe(
        mergeMap(() => {
          return of(Actions.createAction(Actions.EMAIL_VERIFY_RECEIVED))
        }),
        catchAndDispatchCustomError({ email })
      )
    })
  )

export const requestUsernameAndPhonenumberStepPPFEpic = action$ =>
  action$.pipe(
    getPayload(Actions.CHECK_PPF_PLAN_EMAIL_AND_PHONE_REQUESTED),
    switchMap(
      ({
        email,
        user_type,
        urlParams,
        phoneNumber,
        planName,
        planId,
        element
      }) => {
        let queryParams = uriEncodeSearchParams('', {
          username: email.toLowerCase(),
          phone_number: phoneNumber
        })
        return getJSON(`/core/check_username_phone/${queryParams}`).pipe(
          mergeMap(response => {
            if (!response?.can_continue) {
              return of(
                Actions.createAction(
                  Actions.CHECK_PPF_PLAN_EMAIL_AND_PHONE_FAILED
                ),
                Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                  type: eventTypes.formEvent,
                  eventName: event.formEvent.invalid,
                  data: {
                    email: email.toLowerCase()
                  },
                  element,
                  error: formatServerSideError('User already exists')
                })
              )
            } else if (response.phone_number_exists) {
              return of(
                Actions.createAction(Actions.LOGIN_PHONE_NUMBER_REQUESTED, {
                  phoneNumber,
                  isComingFromPPFFlow: true,
                  isExistingUser: true
                })
              )
            } else {
              return of(
                Actions.createAction(
                  Actions.CHECK_PPF_PLAN_EMAIL_AND_PHONE_RECEIVED
                ),
                Actions.createAction(Actions.PPF_PLAN_PHONE_REQUESTED, {
                  phoneNumber: phoneNumber,
                  email: email,
                  planName: planName,
                  planId: planId,
                  element
                })
              )
            }
          }),
          catchError(e => {
            return of(
              Actions.createAction(
                Actions.CHECK_PPF_PLAN_EMAIL_AND_PHONE_FAILED
              )
            )
          })
        )
      }
    )
  )
export const requestSignUpPhoneNumberStepPPFEpic = action$ =>
  action$.pipe(
    getPayload(Actions.PPF_PLAN_PHONE_REQUESTED),
    throttleTime(5000),
    switchMap(
      ({ phoneNumber, email, urlParams, planName, planId, element }) => {
        const data = {
          phone_number: phoneNumber,
          email: email,
          user_type: 1,
          urlParams: urlParams
        }
        return postJSON('/core/register/', data).pipe(
          mergeMap(() => {
            return of(
              Actions.createAction(Actions.PPF_PLAN_PHONE_RECEIVED),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data,
                element
              })
            )
          }),
          catchError(e => {
            return of(
              Actions.createAction(
                Actions.PPF_PLAN_PHONE_RECEIVED_FAIL,
                translateErrorMsg(e),
                e.body?.message ===
                  CUSTOME_ERRORS['There was a problem during signup'] &&
                  Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                    eventName: 'ppf_existing_user',
                    data: { planName: planName, planId: planId }
                  })
              ),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.invalid,
                data,
                element,
                error: formatServerSideError(
                  CUSTOME_ERRORS['There was a problem during signup']
                )
              })
            )
          }),
          catchAndDispatchCustomError(data)
        )
      }
    )
  )

export const checkForUserSubscription = action$ =>
  action$.pipe(
    getPayload(Actions.CHECK_EXISTING_PPF_USER_SUBSCRIPTIONS_REQUESTED),
    switchMap(({ selectedPlanId, planName }) => {
      return getJSON('/core/check_subscription/').pipe(
        mergeMap(response => {
          let actions = [
            Actions.createAction(
              Actions.CHECK_EXISTING_PPF_USER_SUBSCRIPTIONS_RECIEVED,
              response
            )
          ]

          if (!response.plan_exists) {
            actions.push(
              Actions.createAction(Actions.CHECKOUT_SESSION_REQUESTED, {
                customerCoupon: null,
                selectedPlan: { id: selectedPlanId }
              }),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                eventName: 'PPF_redirected_to_payment_sow',
                data: { planName: planName, planId: selectedPlanId }
              })
            )
          } else {
            actions.push(
              Actions.createAction(Actions.SIGNIN_REQUESTED_FROM_PPF_FLOW)
            )
          }
          return of(...actions)
        }),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.CHECK_EXISTING_PPF_USER_SUBSCRIPTIONS_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const requestSignUpPhoneNumberVerifyStepPPFEpic = action$ =>
  action$.pipe(
    getPayload(Actions.PPF_PLAN_PHONE_VERIFY),
    throttleTime(10000),
    switchMap(
      ({ phoneNumber, email, value, element, isPPFUserAlreadyExist }) => {
        const data = {
          email: email,
          phone_number: phoneNumber,
          code: value,
          user_type: 1,
          one_time_login: isPPFUserAlreadyExist
        }
        return postJSON('/core/register_verify/', data).pipe(
          tap(response => {
            setAuthToken(response)
          }),
          mergeMap(() => {
            return of(Actions.createAction(Actions.PPF_PLAN_PHONE_ISVALID))
          }),
          catchError(e => {
            return (
              of(
                Actions.createAction(
                  Actions.PPF_PLAN_PHONE_ISINVALID,
                  translateErrorMsg(e)
                )
              ),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.invalid,
                data,
                element,
                error: formatServerSideError(translateErrorMsg(e))
              })
            )
          }),
          catchAndDispatchCustomError(data)
        )
      }
    )
  )

export const requestSignUpPhoneNumberVerifyStepEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SIGNUP_CODE_VERIFY_REQUESTED),
    withLatestFrom(state$),
    filter(([_, state]) => state.auth.signupFlowType !== signUpFlowTypes.HMO),
    throttleTime(10000),
    switchMap(([{ phoneNumber, email, value, element }, _]) => {
      const data = {
        email: email,
        phone_number: phoneNumber,
        code: value,
        user_type: 1
      }
      return postJSON('/core/register_verify/', data).pipe(
        tap(response => {
          console.log(
            'requestSignUpPhoneNumberVerifyStepEpic response',
            response
          )
        }),
        mergeMap(({ pending_patient }) => {
          if (pending_patient) {
            return of(
              Actions.createAction(Actions.SIGNUP_PANDING_PATIENT_RECEIVED, {
                pending_patient
              }),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: data,
                element
              })
            )
          }
          return of(
            Actions.createAction(Actions.SIGNUP_CODE_VERIFY_SUCCESS),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: data,
              element
            })
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.SIGNUP_CODE_VERIFY_FAILED,
              translateErrorMsg(e)
            )
          )
        }),
        catchAndDispatchCustomError(data, element)
      )
    })
  )

export const requestSignUpPhoneNumberHmoFirstLogin = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SIGNUP_CODE_VERIFY_REQUESTED),
    withLatestFrom(state$),
    filter(([_, state]) => state.auth?.signupFlowType === signUpFlowTypes.HMO),
    switchMap(([{ phoneNumber, value }, state]) => {
      const data = {
        phoneNumber: phoneNumber,
        code: value,
        user: state.signup?.hmoFirstLogin?.userId
      }
      return postJSON('/signup/hmo/verify-sms/', data).pipe(
        mergeMap(({ sms_status, auth_code }) => {
          return of(
            Actions.createAction(Actions.VERIFY_SMS_FIRST_LOGIN_HMO_RECEIVED, {
              sms_status,
              auth_code
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              //todo
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: data,
              element: 'next whats the code'
            })
          )
        }),
        catchAndDispatchCustomError(data, 'next whats the code')
      )
    })
  )

export const requestJoinFamilyPhoneNumberVerifyStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.JOIN_FAMILY_CODE_VERIFY_REQUESTED),
    throttleTime(10000),
    switchMap(({ phoneNumber, email, value }) => {
      const data = {
        email: email,
        phone_number: phoneNumber,
        code: value
      }
      return postJSON('/core/register_verify/', data).pipe(
        mergeMap(() => {
          return of(
            Actions.createAction(Actions.NAVIGATED_TO, 'TermsAndPrivacyScreen')
          )
        }),
        catchAndDispatchCustomError(data)
      )
    })
  )

export const requestOneTimeSMSLoginSetPhoneNumberStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.LOGIN_PHONE_NUMBER_REQUESTED),
    throttleTime(10000),
    switchMap(({ phoneNumber, isComingFromPPFFlow = false }) => {
      const data = {
        phone_number: phoneNumber
      }
      return postJSON('/core/check_phone/', data).pipe(
        mergeMap(response => {
          let actions = [
            Actions.createAction(Actions.LOGIN_PHONE_NUMBER_RECEIVED, response),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: data,
              element: 'Enter_the_verification_code'
            })
          ]
          if (!isComingFromPPFFlow) {
            actions.push(
              Actions.createAction(
                Actions.NAVIGATED_TO,
                routes.VERIFY_ONE_TIME_CODE
              )
            )
          }
          return of(...actions)
        }),
        catchAndDispatchCustomError(data, 'Enter_the_verification_code')
      )
    })
  )

export const requestOneTimeSMSLoginPhoneNumberVerifyStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.ONE_TIME_SMS_LOGIN_CODE_VERIFY_REQUESTED),
    throttleTime(10000),
    switchMap(({ phoneNumber, value }) => {
      const data = {
        phone_number: phoneNumber,
        code: value,
        one_time_login: true
      }
      return postJSON('/core/register_verify/', data).pipe(
        tap(res => {
          setAuthToken(res)
        }),
        mergeMap(() => {
          return of(
            Actions.createAction(Actions.SIGNIN_RECEIVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: data,
              element: 'Sign-in'
            })
          )
        }),
        catchAndDispatchCustomError(data, 'Sign-in')
      )
    })
  )

export const requestSignUpPhoneNumberStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SIGNUP_PHONENUMBER_REQUESTED),
    throttleTime(5000),
    switchMap(
      ({ phoneNumber, email, urlParams, firstName, lastName, dateOfBirth }) => {
        const data = {
          firstName,
          lastName,
          dateOfBirth,
          phone_number: phoneNumber,
          email: email,
          user_type: 1,
          urlParams: urlParams
        }
        return postJSON('/core/register/', data).pipe(
          mergeMap(() => {
            return of(
              Actions.createAction(Actions.SIGNUP_PHONENUMBER_RECEIVED),
              Actions.createAction(
                Actions.SIGNUP_PHONENUMBER_REQUESTED_SUCCESS
              ),
              Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                type: eventTypes.formEvent,
                eventName: event.formEvent.submit,
                data: data,
                element: 'sign-up phone number'
              })
            )
          }),
          catchError(e => {
            return of(
              Actions.createAction(
                Actions.SIGNUP_PHONENUMBER_REQUESTED_FAILED,
                translateErrorMsg(e)
              )
            )
          })
        )
      }
    )
  )

export const requestSignUpAcceptInviteStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.ACCEPT_INVITE_REQUESTED, Actions.JOIN_FAMILY_REQUESTED),
    throttleTime(5000),
    switchMap(({ email }) => {
      const data = {
        email: email
      }
      return postJSON('/core/register_pending/', data).pipe(
        tap(response => {}),
        mergeMap(() => {
          return of(
            Actions.createAction(Actions.SIGNUP_PHONENUMBER_RECEIVED),
            Actions.createAction(Actions.NAVIGATED_TO, 'SignupVerifyCodeScreen')
          )
        }),
        catchAndDispatchCustomError(data)
      )
    })
  )

const formatServerSideError = error => {
  return {
    errorFields: [{ name: ['Server Error'], errors: [error], warnings: [] }]
  }
}

const catchAndDispatchCustomError = (data, element) =>
  catchError(error => {
    console.error(error)
    if (data?.password) {
      delete data['password']
    }
    if (
      // dont move it! This rule heave to be first.
      // non_field_errors is auto genereted djando response keys
      error.body?.non_field_errors &&
      error.body?.non_field_errors[0] ===
        'Unable to log in with provided credentials.'
    ) {
      error.message =
        CUSTOME_ERRORS['Unable to log in with provided credentials.']
      return of(
        Actions.createAction(Actions.OPERATION_FAILED, { error }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: data,
          element,
          error: formatServerSideError(
            CUSTOME_ERRORS['Unable to log in with provided credentials.']
          )
        })
      )
    } else if (
      error.body?.non_field_errors &&
      error.body?.non_field_errors[0] === 'Max login attempts exceeded.'
    ) {
      error.message = CUSTOME_ERRORS['Max login attempts exceeded.']
      return of(
        Actions.createAction(Actions.OPERATION_FAILED, { error }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: data,
          element,
          error: formatServerSideError(
            CUSTOME_ERRORS['Max login attempts exceeded.']
          )
        })
      )
    } else if (error.body?.message === 'There was a problem during signup') {
      return of(
        Actions.createAction(Actions.SIGNUP_EMAIL_FAILED),
        Actions.createAction(Actions.OPERATION_FAILED, {
          signUpFormError: translateErrorMsg(error)
        }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          element,
          data: data,
          error: formatServerSideError(
            CUSTOME_ERRORS['There was a problem during signup']
          )
        })
      )
    } else if (error.body?.detail === 'Not found.') {
      error.message = CUSTOME_ERRORS['Sorry! wrong code']
      return of(
        Actions.createAction(
          Actions.OPERATION_FAILED,
          translateErrorMsg(error)
        ),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          element,
          eventName: event.formEvent.invalid,
          data: data,
          error: formatServerSideError(CUSTOME_ERRORS['Sorry! wrong code'])
        })
      )
    } else {
      return of(
        Actions.createAction(
          Actions.OPERATION_FAILED,
          translateErrorMsg(error)
        ),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          element,
          data: data,
          error: formatServerSideError(translateErrorMsg(error))
        })
      )
    }
  })

export const requestSignInEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SIGNIN_REQUESTED),
    switchMap(({ email, password, element }) => {
      const data = {
        username: email?.toLowerCase(),
        password
      }

      return postJSON('/token-auth/', data).pipe(
        tap(async tokenData => {
          setAuthToken(tokenData)
        }),
        mergeMap(() => {
          delete data['password']
          return of(
            Actions.createAction(Actions.SIGNIN_RECEIVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              element: element,
              data: data
            })
          )
        }),
        catchAndDispatchCustomError(data, element)
      )
    })
  )

export const requestSignOutEpic = action$ =>
  action$.pipe(
    ofType(Actions.SIGNOUT_REQUESTED),
    tap(removeAuthToken),
    mergeMap(() => {
      return of(Actions.createAction(Actions.SIGNOUT_RECEIVED))
    })
  )

export const checkOnboarded = action$ => {
  return action$.pipe(
    ofType(Actions.APP_LAUNCHED),
    switchMap(() =>
      hasOnBoarded().pipe(
        map(hasOnBoarded => {
          return Actions.createAction(Actions.USER_ONBOARDED, {
            hasOnBoarded
          })
        }),
        catchError(() =>
          of(
            Actions.createAction(Actions.USER_ONBOARDED, {
              hasOnBoarded: true
            })
          )
        )
      )
    )
  )
}

export const requestOTPcode = action$ =>
  action$.pipe(
    getPayload(Actions.OTP_CODE_REQUESTED),
    switchMap(({ phone, route }) => {
      return postJSON('/authentication/reset-password/', {
        phone
      }).pipe(
        mergeMap(res => {
          return of(
            Actions.createAction(Actions.OTP_CODE_RECIEVED),
            Actions.createAction(Actions.NAVIGATED_TO, route)
          )
        }),
        catchAndDispatchCustomError({ phone })
      )
    })
  )

export const requestOTPVerifyStepEpic = action$ =>
  action$.pipe(
    getPayload(Actions.OTP_CODE_VERIFY_REQUESTED),
    throttleTime(10000),
    switchMap(({ phone, value, route }) => {
      const data = {
        phone,
        code: value
      }
      return patchJSON('/authentication/reset-password/', data).pipe(
        mergeMap(res => {
          return of(
            Actions.createAction(Actions.OTP_CODE_VERIFY_RECEIVED, {
              ...res,
              route
            })
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.OTP_CODE_VERIFY_REQUEST_FAILURE,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const requestResetPasswordStepEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.RESET_PASSWORD_REQUESTED),
    throttleTime(5000),
    withLatestFrom(state$),
    switchMap(([{ password, token }, state]) => {
      return putJSON(`/authentication/reset-password/${token}/`, {
        password
      }).pipe(
        mergeMap(({ username, password }) => {
          return of(
            Actions.createAction(Actions.RESET_PASSWORD_SUCCESS),
            Actions.createAction(Actions.SIGNIN_REQUESTED, {
              email: username,
              password,
              element: 'reset password signin'
            })
          )
        }),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.RESET_PASSWORD_FAILURE,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const checkAuthentication = action$ => {
  return action$.pipe(
    getPayload(Actions.SIGNIN_RECEIVED),
    switchMap(() =>
      hasAuthToken().pipe(
        mergeMap(() => {
          // if (isMobileWeb()) {
          //   return of(Actions.createAction(Actions.IS_MOBILE_WEB_USER))
          // } else {
          return of(
            Actions.createAction(Actions.CHECK_FOR_VALID_IP, {
              isSignup: false
            })
          )
          // }
        }),
        catchError(() =>
          of(
            Actions.createAction(Actions.IS_SIGNED_IN_RECEIVED, {
              isSignedIn: false
            })
          )
        )
      )
    )
  )
}

export const dispatchLoggedIn = action$ => {
  return action$.pipe(
    getPayload(Actions.IS_SIGNED_IN_RECEIVED),
    filter(({ isSignedIn }) => isSignedIn),
    mapTo(Actions.createAction(Actions.USER_LOGGED_IN_SUCCSESSFULY))
  )
}

export const handleHmoFirstLoginFlow = action$ =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_RECEIVED),
    filter(({ classify_status }) => classify_status === signUpStatus.HMO),
    switchMap(({ user }) => {
      return of(
        Actions.createAction(Actions.HMO_FIRST_LOGIN_FLOW, {
          HashedUserId: user
        })
      )
    })
  )

export const saveUserPaymentRedirectFlow = action$ =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_RECEIVED),
    filter(
      ({ classify_status }) => classify_status === signUpStatus.TELEHEALTH
    ),
    filter(({ user }) => user),
    switchMap(({ user }) => {
      return of(
        Actions.createAction(Actions.PAYEMNT_REDIRECT_FLOW_USER_PROFILE_EXIST, {
          HashedUserId: user
        })
      )
    })
  )

export const handleHmoFirstLoginFlowError = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_RECEIVED),
    withLatestFrom(state$),
    filter(
      ([{ classify_status }, { auth }]) =>
        // if we got telehealth flow but the user declared hmo flow,
        // we need to show error page
        classify_status === signUpStatus.TELEHEALTH &&
        auth?.declareSignUpFlowType === signUpFlowTypes.HMO
    ),
    switchMap(() => {
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.SIGN_UP_ERROR)
      )
    })
  )

export const handleTelehalthSignUpFlow = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_RECEIVED),
    withLatestFrom(state$),
    filter(
      ([{ classify_status }, { auth }]) =>
        // if we got telehealth flow but the  user didn't declare hmo flow,
        // we need to contunue with telehelath flow
        classify_status === signUpStatus.TELEHEALTH &&
        auth?.declareSignUpFlowType !== signUpFlowTypes.HMO
    ),
    mapTo(Actions.createAction(Actions.TELEHEALTH_SIGNUP_FLOW))
  )

export const startTelehealthSignUpFlow = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.TELEHEALTH_SIGNUP_FLOW),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      return of(
        Actions.createAction(Actions.SIGNUP_EMAIL_REQUESTED, {
          email: state?.signup?.userSignup?.email,
          urlParams: state?.signup?.userSignup?.urlParams,
          firstName: state?.signup?.userSignup?.firstName,
          lastName: state?.signup?.userSignup?.lastName,
          phoneNumber: state?.signup?.userSignup?.phoneNumber,
          dateOfBirth: state?.signup?.userSignup?.dob
        })
      )
    })
  )

export const classifyUserContactSupport = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.CLASSIFY_USER_RECEIVED),
    filter(
      ({ classify_status }) =>
        classify_status === signUpStatus.SUPPORT ||
        classify_status === signUpStatus.TRY_AGAIN ||
        classify_status === signUpStatus.LOGIN
    ),
    withLatestFrom(state$),
    switchMap(([{ classify_status }, state]) => {
      return of(
        Actions.createAction(Actions.SHOW_SIGNUP_ERROR_POPUP, {
          text: classify_status
        }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: state?.signup?.userSignup,
          element: 'next information account setup'
        })
      )
    })
  )

export const validSmsHmoFirstLoginRecived = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VERIFY_SMS_FIRST_LOGIN_HMO_RECEIVED),
    withLatestFrom(state$),
    filter(([{ sms_status }, _]) => sms_status === signUpStatus.APPROVED),
    switchMap(([{ auth_code }, { signup }]) => {
      const quickSignUpFlow = signup?.quickSignUpFlow
      const navigateTo = quickSignUpFlow ? routes.CHOOSE_PASSWORD : routes.SSN
      return of(
        Actions.createAction(Actions.VALID_SMS_RECIVED_HMO_FIRST_LOGIN, {
          auth_code
        }),
        Actions.createAction(Actions.NAVIGATED_TO, navigateTo)
      )
    })
  )

export const badSmsHmoFirstLoginRecived = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VERIFY_SMS_FIRST_LOGIN_HMO_RECEIVED),
    filter(({ sms_status }) => sms_status !== signUpStatus.APPROVED),
    withLatestFrom(state$),
    switchMap(([{ sms_status }, state]) => {
      return of(
        Actions.createAction(Actions.SHOW_SIGNUP_ERROR_POPUP, {
          text: sms_status
        }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: state?.signup?.userSignup,
          element: 'next whats the code'
        })
      )
    })
  )

export const validateSsnRequestedHmoFirstLogin = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.VERIFY_SSN_HMO_FIRST_LOGIN_REQUESTED),
    withLatestFrom(state$),
    switchMap(([{ ssnCode }, state]) => {
      const { userId, smsAuthCode } = state?.signup?.hmoFirstLogin
      return postJSON('/signup/hmo/verify-ssn/', {
        ssn: ssnCode,
        user: userId,
        auth_code: smsAuthCode
      }).pipe(
        map(({ ssn_status, auth_code }) =>
          Actions.createAction(Actions.VERIFY_SSN_HMO_FIRST_LOGIN_RECEIVED, {
            ssn_status,
            auth_code
          })
        ),
        catchAndDispatchCustomError({ userId }, 'next ssn')
      )
    })
  )

export const validSsnReceived = action$ =>
  action$.pipe(
    getPayload(Actions.VERIFY_SSN_HMO_FIRST_LOGIN_RECEIVED),
    filter(({ ssn_status }) => ssn_status === signUpStatus.SSN_VALID),
    switchMap(({ auth_code }) => {
      return of(
        Actions.createAction(Actions.NAVIGATED_TO, routes.CHOOSE_PASSWORD),
        Actions.createAction(Actions.VALID_SSN_RECIVED_HMO_FIRST_LOGIN, {
          auth_code
        })
      )
    })
  )

export const badSsnRecieved = (action$, stats$) =>
  action$.pipe(
    getPayload(
      Actions.VERIFY_SSN_HMO_FIRST_LOGIN_RECEIVED,
      Actions.QUICK_SIGNUP_RECEIVED
    ),
    filter(({ ssn_status }) => ssn_status !== signUpStatus.SSN_VALID),
    withLatestFrom(stats$),
    switchMap(([{ ssn_status }, state]) => {
      return of(
        Actions.createAction(Actions.SHOW_SIGNUP_ERROR_POPUP, {
          text: ssn_status
        }),
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: state?.signup?.userSignup,
          element: 'next ssn'
        })
      )
    })
  )

export const qucikSignupEpic = action$ => {
  return action$.pipe(
    getPayload(Actions.QUICK_SIGNUP_REQUESTED),
    switchMap(({ phone, ssn, utm_source, token }) => {
      return postJSON(`/signup/hmo/verify-ssn-quick-signup/`, {
        phoneNumber: phone,
        ssn,
        user: utm_source,
        enrollment_token: token
      }).pipe(
        map(({ ssn_status, auth_code, user, phone }) =>
          Actions.createAction(Actions.QUICK_SIGNUP_RECEIVED, {
            ssn_status,
            auth_code,
            user,
            phone
          })
        ),
        catchError(error => {
          return of(
            Actions.createAction(Actions.SHOW_SIGNUP_ERROR_POPUP, {
              text: signUpStatus.SUPPORT
            }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.invalid,
              element: 'next quick account Setup',
              data: { phone, ssn },
              error: formatServerSideError(translateErrorMsg(error))
            })
          )
        })
      )
    })
  )
}

export const completePaymentRedirectFirstLogin = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SIGNUP_RECEIVED),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        state.auth?.signupFlowType === signUpFlowTypes.TELEHEALTH &&
        !!state.signup?.prefillFieldsForSignup
    ),
    // on payment redirect hmo signup we need to complete only the step for consent agreement
    switchMap(() =>
      of(Actions.createAction(Actions.HMO_CONSENT_AGREEMENT_REQUESTED))
    )
  )

export const completeSingupHmoFirstLogin = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.SIGNUP_PASSWORD_REQUESTED),
    withLatestFrom(state$),
    filter(([_, state]) => state.auth?.signupFlowType === signUpFlowTypes.HMO),
    switchMap(([{ password }, state]) => {
      const { ssnAuthCode, userId, smsAuthCode } = state.signup?.hmoFirstLogin
      const quick_flow = state.signup?.quickSignUpFlow
      return postJSON('/signup/hmo/complete-signup/', {
        password,
        user: userId,
        auth_code: quick_flow ? smsAuthCode : ssnAuthCode,
        quick_flow
      }).pipe(
        // Ensure token is persisted before dispatching authenticated actions
        concatMap(token =>
          from(setAuthToken(token)).pipe(
            tap(() => setUserOnBoarded()),
            mergeMap(() =>
              of(
                Actions.createAction(Actions.HMO_CONSENT_AGREEMENT_REQUESTED), // on hmo signup there is consent aggement, in case the user signed up as hmo user it means he agreed to the consent,
                Actions.createAction(Actions.SIGNUP_RECEIVED),
                Actions.createAction(Actions.CHECK_FOR_VALID_IP, {
                  isSignup: true
                }),
                Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
                  type: eventTypes.formEvent,
                  eventName: event.formEvent.submit,
                  element: 'Choose Password' //todo see if data needed
                })
              )
            )
          )
        ),
        catchError(e =>
          of(
            Actions.createAction(Actions.SHOW_SIGNUP_ERROR_POPUP, {
              text: 'contact_support'
            })
          )
        )
      )
    })
  )

export const resendSMSHmoFirstLogin = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.HMO_RESEND_SMS_REQUESTED),
    withLatestFrom(state$),
    filter(([_, state]) => state.auth?.signupFlowType === signUpFlowTypes.HMO),
    switchMap(([_, state]) => {
      const { userId } = state.signup?.hmoFirstLogin
      return postJSON('/signup/hmo/resend-sms/', {
        user: userId
      }).pipe(
        switchMap(() => {
          return of(
            Actions.createAction(Actions.HMO_RESEND_SMS_RECIEVED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.formEvent,
              eventName: event.formEvent.submit,
              data: state.signup?.userSignup,
              element: 'next send again hmo'
            })
          )
        }),
        catchAndDispatchCustomError({ userId }, 'resend_sms_hmo_flow')
      )
    })
  )

export const redirectToSignupThToHmoEnrolled = action$ =>
  action$.pipe(
    getPayload(Actions.TH_TO_HMO_ENROLLED_SIGNUP_REQUESTED),
    switchMap(() => of(Actions.createAction(Actions.SIGNOUT_REQUESTED)))
  )
